import React, { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { formatPunchTime } from '../lib/timeFormat';
import { useCompanyScope } from '../context/CompanyScopeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { FileText, Check, X, Eye } from 'lucide-react';
import { toast } from 'sonner';

export default function JustificationsReview() {
  const [justifications, setJustifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isCrossCompany, selectedCompanyId } = useCompanyScope();

  useEffect(() => {
    fetchJustifications();
  }, []);

  const filtered = useMemo(() => {
    if (!selectedCompanyId) return justifications;
    return justifications.filter((j) => j.company_id === selectedCompanyId);
  }, [justifications, selectedCompanyId]);

  const fetchJustifications = async () => {
    try {
      const response = await api.get('/attendance/justifications/pending');
      setJustifications(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (just, status) => {
    try {
      if (just.justification_kind === 'absence') {
        await api.post(
          `/attendance/absence-justifications/${just.id}/review?decision=${status}&admin_comment=Revu%20par%20admin`
        );
      } else {
        await api.put(
          `/attendance/justification/${just.id}/review?status=${status}&admin_comment=Revu%20par%20admin`
        );
      }
      toast.success(`Justificatif ${status === 'approved' ? 'approuvé' : 'rejeté'}`);
      fetchJustifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="justifications-review-page">
      <div>
        <h1 className="font-heading font-bold text-3xl text-slate-900">Validation des Justificatifs</h1>
        <p className="text-slate-600 mt-1">Approuver ou rejeter les justificatifs</p>
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Justificatifs En Attente
            <Badge className="ml-2 bg-warning text-white">{filtered.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="font-semibold">Employé</TableHead>
                  {isCrossCompany && <TableHead className="font-semibold">Entreprise</TableHead>}
                  <TableHead className="font-semibold">Type</TableHead>
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="font-semibold">Entrée</TableHead>
                  <TableHead className="font-semibold">Retard (min)</TableHead>
                  <TableHead className="font-semibold">Motif</TableHead>
                  <TableHead className="font-semibold">Fichier</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isCrossCompany ? 9 : 8} className="text-center text-slate-500 py-8">
                      Aucun justificatif en attente
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((just) => (
                    <TableRow key={just.id} className="hover:bg-slate-50">
                      <TableCell className="font-medium">{just.employee_name}</TableCell>
                      {isCrossCompany && (
                        <TableCell>
                          <Badge variant="outline" className="bg-slate-50 text-slate-700">
                            {just.company_name || '-'}
                          </Badge>
                        </TableCell>
                      )}
                      <TableCell>
                        {just.justification_kind === 'absence' ? (
                          <Badge className="bg-red-500 text-white">Absence</Badge>
                        ) : (
                          <Badge className="bg-orange-500 text-white">Retard</Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-mono">{just.date}</TableCell>
                      <TableCell>
                        {just.check_in ? formatPunchTime(just.check_in) : '-'}
                      </TableCell>
                      <TableCell>
                        {just.justification_kind === 'absence'
                          ? <span className="text-slate-400">—</span>
                          : <Badge className="bg-warning text-white">{just.late_minutes || 0}</Badge>}
                      </TableCell>
                      <TableCell className="max-w-xs truncate" title={just.justification_reason}>
                        {just.justification_reason}
                      </TableCell>
                      <TableCell>
                        {just.justification_file ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.open(`/api/uploads/justifications/${just.justification_file}`, '_blank')}
                            data-testid={`view-file-${just.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Voir
                          </Button>
                        ) : (
                          <span className="text-xs text-slate-400">Aucun fichier</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-success hover:bg-green-50"
                            onClick={() => handleReview(just, 'approved')}
                            data-testid={`approve-${just.id}`}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-error hover:bg-red-50"
                            onClick={() => handleReview(just, 'rejected')}
                            data-testid={`reject-${just.id}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}